'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { AuthEventKeys, AuthEvents } from '../events';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import { z } from 'zod';
import {
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
  Button,
  Link,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Grow,
} from '@mui/material';
import ArrowBackFilled from '@mui/icons-material/ArrowBack';
import { EventBus } from '@/shared/utils';
import { Auth as AuthDict } from '@/shared/i18n';
import { Auth } from 'firebase/auth';
import { IUBENDA_INTERNAL_PAGES } from 'src/shared/utils/iubenda-urls';

type RegisterFormProps = {
  eventBus: EventBus<AuthEvents>;

  dict: AuthDict;

  auth: Auth;
};

function RegisterForm({ eventBus, dict, auth }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const resetOnMount = () => {
    setEmail('');
    setPassword('');
    setName('');
    setError(null);
    setEmailError(null);
    setPasswordError(null);
    setIsChecked(false);
  };

  useEffect(() => {
    resetOnMount();
  }, []);

  const subscribeToRegisterEvents = useCallback(() => {
    const handleSuccess = () => {
      setLoading(false);
    };
    const handleError = (error: Error) => {
      setError(error.message);
      setLoading(false);

      if (error.message.toLowerCase().includes('email')) {
        setEmailError(error.message);
        setPasswordError(null);
      } else if (error.message.toLowerCase().includes('password')) {
        setPasswordError(error.message);
        setEmailError(null);
      } else {
        setEmailError(null);
        setPasswordError(null);
      }
    };
    eventBus.on(AuthEventKeys.register.success, handleSuccess);
    eventBus.on(AuthEventKeys.register.error, handleError);
    return () => {
      eventBus.off(AuthEventKeys.register.success, handleSuccess);
      eventBus.off(AuthEventKeys.register.error, handleError);
    };
  }, [eventBus]);

  useEffect(() => {
    const cleanup = subscribeToRegisterEvents();
    return cleanup;
  }, [subscribeToRegisterEvents]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Schema di validazione con Zod utilizzando il dizionario
  const registerSchema = z.object({
    email: z
      .string()
      .email({ message: dict.auth_errors?.invalid_email || "Inserisci un'email valida" }),
    password: z.string().min(8, {
      message: dict.auth_errors?.short_password,
    }),
    name: z
      .string()
      .min(2, { message: dict.auth_errors?.short_name || 'Il nome deve avere almeno 2 caratteri' }),
  });

  const containsNumber = (str: string) => /\d/.test(str);

  const isFormValid = () => {
    return (
      email.length > 0 &&
      password.length >= 8 &&
      containsNumber(password) &&
      name.length >= 2 &&
      isChecked
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const userInfo = { email, password, name };
    const result = registerSchema.safeParse(userInfo);
    if (!result.success) {
      const firstError = result.error.errors[0];
      setError(firstError.message);
      if (firstError.path.includes('email')) {
        setEmailError(firstError.message);
        setPasswordError(null);
      } else if (firstError.path.includes('password')) {
        setPasswordError(firstError.message);
        setEmailError(null);
      } else if (firstError.path.includes('name')) {
        setError(firstError.message);
      }
      return;
    }
    setError(null);
    setEmailError(null);
    setPasswordError(null);
    setLoading(true);
    eventBus.emit(AuthEventKeys.register.do, { email, password, name, auth });
  };

  const handleShowLogin = () => {
    eventBus.emit(AuthEventKeys.login.view, undefined);
  };

  return (
    <form className="px-6 py-8 md:px-16 md:py-12 rounded-xl bg-paper" onSubmit={handleSubmit}>
      <div className="space-y-2 text-center">
        <Typography variant="mac-sans-h3" component="h1" color="textPrimary">
          {dict.register?.title || 'Crea nuovo account'}
        </Typography>
        <Typography variant="body-500" component="p" color="textSecondary">
          {dict.register?.description || 'Compila i campi per creare il tuo account'}
        </Typography>
      </div>
      <div className="flex flex-col w-full mt-12">
        <Grow
          in={!!(error && !emailError && !passwordError)}
          mountOnEnter
          unmountOnExit
          style={{ transformOrigin: 'center center' }}
          timeout={500}
        >
          <Alert variant="filled" severity="error" className="mb-6">
            {error}
          </Alert>
        </Grow>
        <div className="space-y-6">
          <TextField
            placeholder={dict.register?.name_placeholder || 'Il tuo nome e cognome'}
            variant="outlined"
            size="medium"
            className="w-full"
            label={
              <Typography variant="s2">{dict.register?.name_label || 'Nome e cognome'}</Typography>
            }
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error && !emailError && !passwordError}
            helperText={!!error && !emailError && !passwordError ? error : ''}
          />
          <TextField
            placeholder={dict.register?.email_placeholder || 'La tua migliore e-mail'}
            variant="outlined"
            size="medium"
            className="w-full"
            label={
              <Typography variant="s2">
                {dict.register?.email_label || 'Indirizzo e-mail'}
              </Typography>
            }
            value={email}
            onChange={(e) => setEmail(e.target.value?.toLowerCase())}
            error={!!emailError}
            helperText={emailError || ''}
          />
          <FormControl variant="outlined" className="w-full" error={!!passwordError}>
            <InputLabel htmlFor="outlined-adornment-password">
              <Typography variant="s2">{dict.register?.password_label || 'Password'}</Typography>
            </InputLabel>
            <OutlinedInput
              placeholder="La tua password"
              id="outlined-adornment-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label={dict.register?.password_label || 'Password'}
            />
            <FormHelperText>
              <Typography variant="caption-small">
                {passwordError ||
                  dict.register?.password_helper ||
                  'La password deve avere almeno 8 caratteri e contenere un numero'}
              </Typography>
            </FormHelperText>
          </FormControl>
        </div>
        <span className="flex flex-col w-full gap-4 mt-6">
          <FormControlLabel
            required
            labelPlacement="end"
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
              />
            }
            label={
              <span className="inline-flex flex-wrap justify-center gap-2 mr-1 font-semibold text-text-secondary">
                <Typography variant="button-s" component="p">
                  {dict.register?.terms_text_prefix || 'Iscrivendoti accetti la'}
                </Typography>
                <Link
                  color="primary"
                  underline="none"
                  target="_blank"
                  href={`${process.env.NEXT_PUBLIC_FRONT_END_HOST}/${IUBENDA_INTERNAL_PAGES.PRIVACY_POLICY}`}
                  variant="button-s"
                >
                  {dict.register?.privacy_policy || 'Privacy Policy'}
                </Link>
                <Typography variant="button-s" component="p">
                  {dict.register?.terms_text_middle || 'e'}
                </Typography>
                <Link
                  color="primary"
                  underline="none"
                  target="_blank"
                  href={`${process.env.NEXT_PUBLIC_FRONT_END_HOST}/${IUBENDA_INTERNAL_PAGES.TERMS_OF_SERVICE}`}
                  variant="button-s"
                >
                  {dict.register?.terms_of_service || 'Termini del servizio'}
                </Link>
              </span>
            }
          />
        </span>
        <Button
          size="large"
          variant="contained"
          color="primary"
          type="submit"
          className="w-full my-4"
          disabled={!isFormValid() || loading}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <Typography variant="button-l">{dict.register?.button_text || 'Iscriviti'}</Typography>
          )}
        </Button>
        <Button
          size="large"
          variant="text"
          color="primary"
          className="w-full gap-2"
          onClick={handleShowLogin}
        >
          <ArrowBackFilled />
          <Typography variant="button-l">
            {dict.register?.back_to_login || 'Torna al login'}
          </Typography>
        </Button>
      </div>
    </form>
  );
}

export default RegisterForm;
